/**
 * Matching Engine for Gmail AI Quotation Integration
 *
 * Matches AI-extracted buyer/item data against existing QuickBill records
 * using exact + fuzzy similarity algorithms.
 */

import type {
  BuyerMatchCandidate,
  ExtractedBuyer,
  ExtractedLineItem,
  ItemMatchCandidate,
  MatchResult,
} from "@/types/gmail";

// ==================== Levenshtein Distance ====================

function levenshteinDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, (_, i) =>
    Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j - 1], dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }

  return dp[m][n];
}

function normalizedSimilarity(a: string, b: string): number {
  const aLower = a.toLowerCase().trim();
  const bLower = b.toLowerCase().trim();
  if (aLower === bLower) return 1.0;
  const maxLen = Math.max(aLower.length, bLower.length);
  if (maxLen === 0) return 1.0;
  const dist = levenshteinDistance(aLower, bLower);
  return 1 - dist / maxLen;
}

// ==================== Keyword Overlap ====================

function tokenize(text: string): Set<string> {
  return new Set(
    text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter((t) => t.length > 2)
  );
}

function jaccardSimilarity(a: string, b: string): number {
  const tokensA = tokenize(a);
  const tokensB = tokenize(b);
  if (tokensA.size === 0 && tokensB.size === 0) return 1.0;
  const intersection = new Set([...tokensA].filter((t) => tokensB.has(t)));
  const union = new Set([...tokensA, ...tokensB]);
  return intersection.size / union.size;
}

function combinedSimilarity(a: string, b: string): number {
  const lev = normalizedSimilarity(a, b);
  const jac = jaccardSimilarity(a, b);
  // Weight: 60% Levenshtein + 40% Jaccard
  return lev * 0.6 + jac * 0.4;
}

// ==================== Buyer Matching ====================

export interface BuyerRecord {
  id: string;
  buyerCode: string | null;
  buyerName: string;
  buyerEmail: string;
  buyerRegistrationType: string;
  buyerRegistrationNumber: string;
  buyerTin: string;
  buyerCity: string;
  buyerState: string;
  buyerCountry: string;
}

const BUYER_EMAIL_CONFIDENCE = 1.0;
const BUYER_NAME_THRESHOLD = 0.75;

export function matchBuyer(
  extracted: ExtractedBuyer,
  buyers: BuyerRecord[]
): MatchResult<BuyerMatchCandidate> {
  const candidates: BuyerMatchCandidate[] = [];

  for (const buyer of buyers) {
    let similarity = 0;

    // Exact email match = 100% confidence
    if (
      extracted.buyer_email &&
      buyer.buyerEmail &&
      extracted.buyer_email.toLowerCase().trim() === buyer.buyerEmail.toLowerCase().trim()
    ) {
      return {
        type: "exact",
        confidence: BUYER_EMAIL_CONFIDENCE,
        matches: [{ ...buyer, similarity: 1.0 }],
      };
    }

    // Company name / buyer name fuzzy match
    const extractedName =
      extracted.buyer_company_name || extracted.buyer_name || "";
    if (extractedName && buyer.buyerName) {
      similarity = combinedSimilarity(extractedName, buyer.buyerName);
    }

    if (similarity >= BUYER_NAME_THRESHOLD) {
      candidates.push({ ...buyer, similarity });
    }
  }

  if (candidates.length === 0) {
    return { type: "none", confidence: 0, matches: [] };
  }

  // Sort by similarity descending
  candidates.sort((a, b) => b.similarity - a.similarity);

  return {
    type: "fuzzy",
    confidence: candidates[0].similarity,
    matches: candidates.slice(0, 5), // Top 5 candidates
  };
}

// ==================== Item Matching ====================

export interface ItemRecord {
  id: string;
  itemCode: string;
  description: string;
  classification: string;
  taxType: string;
  taxRate: number;
  unitPrice: number;
  measurement: string | null;
}

const ITEM_CODE_EXACT_CONFIDENCE = 1.0;
const ITEM_NAME_THRESHOLD = 0.50;

export function matchItem(
  extracted: ExtractedLineItem,
  items: ItemRecord[]
): MatchResult<ItemMatchCandidate> {
  const candidates: ItemMatchCandidate[] = [];

  for (const item of items) {
    let similarity = 0;

    // Exact item code match
    if (
      extracted.product_code &&
      item.itemCode &&
      extracted.product_code.toLowerCase().trim() === item.itemCode.toLowerCase().trim()
    ) {
      return {
        type: "exact",
        confidence: ITEM_CODE_EXACT_CONFIDENCE,
        matches: [{ ...item, similarity: 1.0 }],
      };
    }

    // Description keyword overlap
    const extractedDesc = extracted.item_desc || "";
    if (extractedDesc && item.description) {
      similarity = combinedSimilarity(extractedDesc, item.description);
    }

    if (similarity >= ITEM_NAME_THRESHOLD) {
      candidates.push({ ...item, similarity });
    }
  }

  if (candidates.length === 0) {
    return { type: "none", confidence: 0, matches: [] };
  }

  candidates.sort((a, b) => b.similarity - a.similarity);

  return {
    type: "fuzzy",
    confidence: candidates[0].similarity,
    matches: candidates.slice(0, 5),
  };
}

// ==================== Batch matching ====================

export interface LineItemMatchResult {
  extractedItem: ExtractedLineItem;
  matchResult: MatchResult<ItemMatchCandidate>;
}

export function matchAllLineItems(
  extractedItems: ExtractedLineItem[],
  items: ItemRecord[]
): LineItemMatchResult[] {
  return extractedItems.map((extracted) => ({
    extractedItem: extracted,
    matchResult: matchItem(extracted, items),
  }));
}
